import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTheme } from '@mui/material/styles';
import { usePageTracking } from '@monorepo/client-common';
import { Footer, Header, Main } from '@monorepo/hub-common';
import { BreadcrumbsProvider } from '@monorepo/nova-ui';
import { Flex } from '@monorepo/react-components';
import { AnalyticsEventTypes } from '@monorepo/types';
import { CreditEventsModalProvider } from '../../contexts/credit-events-modal';
import { getProject } from '../../helpers/get-project';
import { useStore } from '../../helpers/use-store';
import AuthenticatedExtras from '../../routers/authenticated-extras';
import gtmService from '../../services/analytics/analytics';
import { KycVerification } from '../user-verification';
import HeaderBanner from './header-banner';
import { useCartData } from './hooks/use-cart-data';

import './styles/main.scss';

export type LayoutVariant = 'default' | 'trade-talk';

interface LayoutProps {
  variant?: LayoutVariant;
  fullScreen?: boolean;
}

const Layout = ({ fullScreen = false, variant = 'default' }: LayoutProps) => {
  const {
    dataStore: { applicationConfigStore, userStore },
    uiStore,
  } = useStore();

  const cartData = useCartData();

  // Standardized page tracking with hub_ prefixed page names
  usePageTracking();

  const { banners } = applicationConfigStore.applicationConfig;
  const location = useLocation();
  const { is5ers } = getProject();
  const theme = useTheme();

  // Variant-specific logic
  const isTradeTalkVariant = variant === 'trade-talk';

  // Check if gradient should be applied (dark mode + 5ers, but NOT for trade-talk variant)
  const hasGradientBackground =
    theme.palette.mode === 'dark' && is5ers && !isTradeTalkVariant;

  // Header is transparent for trade-talk variant OR when gradient is applied
  const isHeaderTransparent = isTradeTalkVariant || hasGradientBackground;

  // Theme-adaptive gradient backgrounds
  const getLayoutBackground = () => {
    if (isTradeTalkVariant) {
      return 'var(--mui-palette-background-contrast)';
    }
    if (hasGradientBackground) {
      // Dark mode gradient - keep similar aesthetic but adapt to dark theme
      return `radial-gradient(25% 50% at 75% 2%, #65344E 0%, #2F1749 53.83%, #000000 100%)`;
    }
    return 'transparent';
  };

  useEffect(() => {
    gtmService.sendEvent({
      event: AnalyticsEventTypes.PageView,
      params: {
        title: location.pathname,
        location: window.location.href,
      },
    });

    // Clear initialUrl when we've successfully navigated to it
    // React Router's Navigate component won't redirect if already on target path, so this is safe
    // Only depend on location.pathname to avoid unnecessary re-runs when other uiStore observables change
    if (uiStore.initialUrl && location.pathname === uiStore.initialUrl) {
      uiStore.clearInitialUrl();
    }
  }, [location.pathname]);

  return (
    <CreditEventsModalProvider cartData={cartData}>
      <BreadcrumbsProvider>
        <div
          className="layout-new"
          style={{
            background: getLayoutBackground(),
            ...(isTradeTalkVariant && { height: 'unset' }),
          }}
        >
          <HeaderBanner banners={banners} />
          <Header
            cartData={cartData}
            isTransparent={isHeaderTransparent}
            userId={userStore.currentUser?.userId}
          />
          <KycVerification />
          <Main className="content">
            <Flex
              flex={1}
              className={`main-content ${fullScreen ? 'main-content--fullscreen' : ''}`}
              vertical
            >
              <Outlet />
            </Flex>
          </Main>
          <Footer />
          <AuthenticatedExtras />
        </div>
      </BreadcrumbsProvider>
    </CreditEventsModalProvider>
  );
};

export default observer(Layout);
